'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	HYPERLIQUID_MIN_DEPOSIT_USDC,
	HYPERLIQUID_MIN_ORDER_NOTIONAL_USD,
} from '@/constants/hyperliquid'
import type { BridgePlatform } from '@/hooks/useInternalTransfer'
import { useInternalTransfer } from '@/hooks/useInternalTransfer'
import { CHAIN_CONFIG, getExplorerUrl } from '@/lib/api/relay'
import { useTrading } from '@/providers/TradingProvider'
import { useModalStore } from '@/stores/modal-store'
import { AlertCircle, ArrowDown, CheckCircle2, ExternalLink, Info, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

const PLATFORM_CONFIG: Record<
	BridgePlatform,
	{ label: string; logo: string; token: string; chainId: number }
> = {
	polymarket: {
		label: 'Polymarket',
		logo: '/logos/polymarket.jpeg',
		token: 'pUSD',
		chainId: CHAIN_CONFIG.polygon.id,
	},
	kalshi: {
		label: 'Kalshi',
		logo: '/logos/kalshi.jpeg',
		token: 'USDC',
		chainId: CHAIN_CONFIG.solana.id,
	},
	predictfun: {
		label: 'Predict.fun',
		logo: '/logos/predict.png',
		token: 'USDT',
		chainId: CHAIN_CONFIG.bsc.id,
	},
	limitless: {
		label: 'Limitless',
		logo: '/logos/limitless.png',
		token: 'USDC',
		chainId: CHAIN_CONFIG.base.id,
	},
	myriad: {
		label: 'Myriad',
		logo: '/logos/myriad.jpg',
		token: 'USDT',
		chainId: CHAIN_CONFIG.bsc.id,
	},
	hyperliquid: {
		label: 'Hyperliquid',
		logo: '/logos/hyperliquid.png',
		token: 'USDC',
		chainId: CHAIN_CONFIG.arbitrum.id,
	},
}

function getOtherPlatforms(current: BridgePlatform): BridgePlatform[] {
	const all: BridgePlatform[] = [
		'polymarket',
		'kalshi',
		'predictfun',
		'myriad',
		'limitless',
		'hyperliquid',
	]
	return all.filter(p => p !== current)
}

export function BridgeModal() {
	const t = useTranslations('bridge')
	const tCommon = useTranslations('common')
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'bridge'

	const { relayClient, isReady, status: tradingStatus, error: tradingError } = useTrading()

	const { getBalance, quote, fetchQuote, execute, status, error, txHash, reset } =
		useInternalTransfer()

	const [fromPlatform, setFromPlatform] = useState<BridgePlatform>('polymarket')
	const [toPlatform, setToPlatform] = useState<BridgePlatform>('kalshi')
	const [amount, setAmount] = useState('')
	const [isMaxAmount, setIsMaxAmount] = useState(false)

	const fromBalance = getBalance(fromPlatform)
	const toBalance = getBalance(toPlatform)
	const fromConfig = PLATFORM_CONFIG[fromPlatform]
	const toConfig = PLATFORM_CONFIG[toPlatform]

	const amountNum = Number.parseFloat(amount) || 0
	// Bridge2 on Arbitrum requires ≥5 USDC. Amounts below the minimum are
	// permanently lost (validators never credit them on HyperCore). Block
	// the form on this case explicitly so the user can't strand funds.
	const belowHyperliquidMin =
		toPlatform === 'hyperliquid' && amountNum > 0 && amountNum < HYPERLIQUID_MIN_DEPOSIT_USDC
	// $5-$10 is depositable but unspendable — HL outcome markets reject orders
	// below $10 notional. Warn (non-blocking) so the user can choose to top up.
	const belowHyperliquidTradeMin =
		toPlatform === 'hyperliquid' &&
		amountNum >= HYPERLIQUID_MIN_DEPOSIT_USDC &&
		amountNum < HYPERLIQUID_MIN_ORDER_NOTIONAL_USD
	const isValidAmount =
		amountNum > 0 && (isMaxAmount || amountNum <= fromBalance) && !belowHyperliquidMin

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setAmount('')
			setFromPlatform('polymarket')
			setToPlatform('kalshi')
			setIsMaxAmount(false)
			reset()
		}
	}, [isOpen, reset])

	const handleMax = useCallback(() => {
		setAmount(fromBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [fromBalance])

	const handleSwapPlatforms = useCallback(() => {
		const prevFrom = fromPlatform
		const prevTo = toPlatform
		setFromPlatform(prevTo)
		setToPlatform(prevFrom)
		setAmount('')
		setIsMaxAmount(false)
		reset()
	}, [fromPlatform, toPlatform, reset])

	const handleFromChange = useCallback(
		(val: string) => {
			const newFrom = val as BridgePlatform
			setFromPlatform(newFrom)
			// If to is the same as new from, swap
			if (toPlatform === newFrom) {
				const others = getOtherPlatforms(newFrom)
				setToPlatform(others[0])
			}
			setAmount('')
			setIsMaxAmount(false)
			reset()
		},
		[toPlatform, reset]
	)

	const handleToChange = useCallback(
		(val: string) => {
			const newTo = val as BridgePlatform
			setToPlatform(newTo)
			reset()
		},
		[reset]
	)

	const handleGetQuote = useCallback(async () => {
		if (!isValidAmount) {
			return
		}
		await fetchQuote({
			amount,
			fromPlatform,
			toPlatform,
			useMaxBalance: isMaxAmount,
		})
	}, [amount, fromPlatform, toPlatform, isValidAmount, fetchQuote, isMaxAmount])

	const polymarketSessionBlocked = fromPlatform === 'polymarket' && !isReady

	const handleConfirm = useCallback(async () => {
		if (polymarketSessionBlocked) {
			return
		}
		await execute(relayClient)
	}, [execute, relayClient, polymarketSessionBlocked])

	const handleClose = useCallback(() => {
		if (status === 'executing') {
			return
		}
		closeModal()
	}, [status, closeModal])

	const formatBalance = (value: number) => {
		return value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
	}

	const renderPlatformSelectItem = (platform: BridgePlatform) => {
		const config = PLATFORM_CONFIG[platform]
		return (
			<SelectItem key={platform} value={platform}>
				<div className="flex items-center gap-2">
					<img
						src={config.logo}
						alt={config.label}
						width={16}
						height={16}
						className="rounded-[4px]"
					/>
					{config.label}
				</div>
			</SelectItem>
		)
	}

	// Determine the explorer chain for tx hash display
	const getExplorerChainId = () => {
		if (!quote) return CHAIN_CONFIG.polygon.id
		return PLATFORM_CONFIG[quote.fromPlatform].chainId
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonContainerClasses="top-[14px] size-[26px]"
				className="gap-[24px] rounded-3xl border border-white/10 bg-brand-dark-light p-[16px] sm:max-w-[512px]"
			>
				<DialogHeader>
					<DialogTitle className="text-center text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
				</DialogHeader>

				{status === 'idle' || status === 'loading_quote' || status === 'error' ? (
					<div className="flex flex-col gap-[24px]">
						{/* From Section */}
						<div className="flex flex-col gap-[12px]">
							<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
								<div className="flex items-center justify-between">
									<p className=" text-[16px] text-white/70 leading-[100%]">{t('from')}</p>
									<Select value={fromPlatform} onValueChange={handleFromChange}>
										<SelectTrigger className="!bg-transparent h-auto w-auto gap-[8px] border-0 p-0 text-[14px] text-white outline-0">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{renderPlatformSelectItem('polymarket')}
											{renderPlatformSelectItem('kalshi')}
											{renderPlatformSelectItem('predictfun')}
											{renderPlatformSelectItem('myriad')}
											{renderPlatformSelectItem('limitless')}
											{renderPlatformSelectItem('hyperliquid')}
										</SelectContent>
									</Select>
								</div>

								<div className="flex items-center justify-between gap-[12px]">
									<div className="flex items-center gap-[8px] overflow-hidden">
										<input
											type="text"
											inputMode="decimal"
											value={amount}
											onChange={e => {
												const value = e.target.value
												if (value === '' || /^\d*\.?\d*$/.test(value)) {
													setAmount(value)
													setIsMaxAmount(false)
												}
											}}
											placeholder="0"
											className="w-full bg-transparent text-[48px] text-white leading-[100%] [appearance:textfield] placeholder:text-white/30 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
										<span className="shrink-0 font-medium text-[24px] text-white/50 leading-[100%] tracking-[0.25px]">
											{fromConfig.token}
										</span>
									</div>
									<button
										type="button"
										onClick={handleMax}
										className="shrink-0 rounded-[12px] bg-white/5 px-[16px] py-[10px] text-[14px] text-white leading-[100%] transition-colors hover:bg-white/20"
									>
										{t('max')}
									</button>
								</div>

								<div className="flex items-center justify-between">
									<p className=" text-[14px] text-white/75 leading-[100%]">{t('balance')}</p>
									<p className=" text-[14px] text-white/75 leading-[100%]">
										{formatBalance(fromBalance)} {fromConfig.token}
									</p>
								</div>

								{!isMaxAmount && amountNum > fromBalance && (
									<p className="text-red-500 text-xs">{t('insufficientBalance')}</p>
								)}
							</div>

							{/* Swap Arrow */}
							<div className="relative flex justify-center">
								<button
									type="button"
									onClick={handleSwapPlatforms}
									className="-top-[24px] absolute z-10 flex h-[40px] w-[40px] items-center justify-center rounded-full border-[3px] border-brand-gray bg-brand-dark-light transition-colors hover:bg-white/10"
								>
									<ArrowDown className="h-5 w-5 text-white" />
								</button>
							</div>

							{/* To Section */}
							<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
								<div className="flex items-center justify-between">
									<p className=" text-[16px] text-white/70 leading-[100%]">{t('to')}</p>
									<Select value={toPlatform} onValueChange={handleToChange}>
										<SelectTrigger className="!bg-transparent h-auto w-auto gap-[8px] border-0 p-0 text-[14px] text-white outline-0">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{getOtherPlatforms(fromPlatform).map(p => renderPlatformSelectItem(p))}
										</SelectContent>
									</Select>
								</div>

								<div className="flex items-center justify-between gap-[12px]">
									<p className=" text-[48px] text-white leading-[100%]">{amount || '0'}</p>
									<span className="shrink-0 font-medium text-[24px] text-white/50 leading-[100%] tracking-[0.25px]">
										{toConfig.token}
									</span>
								</div>

								<div className="flex items-center justify-between">
									<p className=" text-[14px] text-white/75 leading-[100%]">{t('balance')}</p>
									<p className=" text-[14px] text-white/75 leading-[100%]">
										{formatBalance(toBalance)} {toConfig.token}
									</p>
								</div>
							</div>
						</div>

						{/* Error */}
						{error && (
							<div className="flex items-center gap-[12px] rounded-[12px] border border-red-500/20 bg-red-500/10 p-[16px]">
								<AlertCircle className="h-5 w-5 shrink-0 text-red-400" />
								<p className="overflow-hidden text-[14px] text-red-400 text-wrapper leading-[140%]">
									{error}
								</p>
							</div>
						)}

						{/* Hyperliquid minimum warning */}
						{belowHyperliquidMin && !error && (
							<div className="flex items-center gap-[12px] rounded-[12px] border border-yellow-500/20 bg-yellow-500/10 p-[16px]">
								<AlertCircle className="h-5 w-5 shrink-0 text-yellow-400" />
								<p className="overflow-hidden text-[14px] text-wrapper text-yellow-400 leading-[140%]">
									Hyperliquid Bridge2 minimum is ${HYPERLIQUID_MIN_DEPOSIT_USDC} USDC. Smaller
									amounts are permanently lost by validators. Enter ${HYPERLIQUID_MIN_DEPOSIT_USDC}{' '}
									or more.
								</p>
							</div>
						)}

						{/* $10 trading minimum hint — non-blocking, just informational */}
						{belowHyperliquidTradeMin && !error && (
							<div className="flex items-center gap-[12px] rounded-[12px] border border-yellow-500/20 bg-yellow-500/10 p-[16px]">
								<AlertCircle className="h-5 w-5 shrink-0 text-yellow-400" />
								<p className="overflow-hidden text-[14px] text-wrapper text-yellow-400 leading-[140%]">
									Bridge will succeed, but HL outcome markets require $
									{HYPERLIQUID_MIN_ORDER_NOTIONAL_USD} minimum per order — ${amountNum} will land on
									HL but can't be traded until topped up.
								</p>
							</div>
						)}

						{/* Info Banner */}
						<div className="flex items-center gap-[12px] rounded-[12px] bg-[#2B2117] p-[16px]">
							<Info className="h-5 w-5 shrink-0 text-[#D79B28]" />
							<p className=" text-[#D79B28] text-[14px] leading-[140%]">{t('transferInfo')}</p>
						</div>

						{/* Bridge Button */}
						<Button
							onClick={handleGetQuote}
							disabled={!isValidAmount || status === 'loading_quote'}
							className="h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
						>
							{status === 'loading_quote' ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t('gettingQuote')}
								</>
							) : (
								t('continue')
							)}
						</Button>
					</div>
				) : status === 'quote_ready' && quote ? (
					/* Quote Confirmation View */
					<div className="flex flex-col gap-[24px]">
						<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="flex items-center justify-between">
								<div className="text-center">
									<p className=" text-[14px] text-white/70 leading-[100%]">
										{PLATFORM_CONFIG[quote.fromPlatform].label}
									</p>
									<p className="mt-[8px] font-bold text-[24px] text-white leading-[100%]">
										-${quote.inputAmount}
									</p>
								</div>
								<ArrowDown className="-rotate-90 h-5 w-5 text-brand-blue" />
								<div className="text-center">
									<p className=" text-[14px] text-white/70 leading-[100%]">
										{PLATFORM_CONFIG[quote.toPlatform].label}
									</p>
									<p className="mt-[8px] font-bold text-[24px] text-emerald-400 leading-[100%]">
										+${quote.estimatedOutput}
									</p>
								</div>
							</div>
						</div>
						{/* Fee Details */}
						<div className="flex flex-col gap-[12px]">
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('networkFee')}</p>
								<p className=" text-[14px] text-emerald-400 leading-[100%]">{t('sponsored')}</p>
							</div>
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('relayFee')}</p>
								<p className=" text-[14px] text-white leading-[100%]">${quote.relayFeeUsd}</p>
							</div>
							{Number(quote.hlBridgeFeeUsd) > 0 && (
								<div className="flex items-center justify-between">
									<p className=" text-[14px] text-white/75 leading-[100%]">HL Bridge Fee</p>
									<p className=" text-[14px] text-white leading-[100%]">${quote.hlBridgeFeeUsd}</p>
								</div>
							)}
							<div className="flex items-center justify-between">
								<p className=" text-[14px] text-white/75 leading-[100%]">{t('estTime')}</p>
								<p className=" text-[14px] text-white leading-[100%]">
									{quote.timeEstimate >= 60
										? `~${Math.round(quote.timeEstimate / 60)} min`
										: `~${quote.timeEstimate}s`}
								</p>
							</div>
						</div>

						{/* Polymarket trading-session warning — surface the silent guard
						    so the user sees why "Confirm" doesn't fire. */}
						{polymarketSessionBlocked && (
							<div className="flex items-start gap-[12px] rounded-[12px] border border-amber-500/30 bg-amber-500/10 p-[12px]">
								<AlertCircle className="mt-[2px] h-4 w-4 shrink-0 text-amber-400" />
								<p className="text-[12px] text-amber-300 leading-[140%]">
									Polymarket trading session is not ready (status: {tradingStatus}
									{tradingError ? ` — ${tradingError.message}` : ''}). Bridge from Polymarket is
									blocked until the deposit wallet is deployed and approvals are set on-chain.
								</p>
							</div>
						)}

						{/* Action Buttons */}
						<div className="flex gap-[12px]">
							<Button
								variant="outline"
								onClick={reset}
								className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
							>
								{tCommon('back')}
							</Button>
							<Button
								onClick={handleConfirm}
								disabled={polymarketSessionBlocked}
								className="h-12 flex-1 rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{t('confirmBridge')}
							</Button>
						</div>
					</div>
				) : status === 'executing' ? (
					/* Executing View */
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								{t('processingBridge')}
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								{t('movingFundsTo', {
									platform: quote ? PLATFORM_CONFIG[quote.toPlatform].label : '',
								})}
							</p>
						</div>
						<p className=" text-[14px] text-white/50 leading-[100%]">{t('confirmWalletPrompts')}</p>
					</div>
				) : status === 'success' && quote ? (
					/* Success View */
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<CheckCircle2 className="h-12 w-12 text-emerald-500" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								{t('bridgeComplete')}
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								{t('transferredTo', {
									amount: quote.estimatedOutput,
									platform: PLATFORM_CONFIG[quote.toPlatform].label,
								})}
							</p>
						</div>
						{txHash && (
							<a
								href={getExplorerUrl(getExplorerChainId(), txHash)}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-[4px] rounded-[8px] bg-white/5 px-[12px] py-[6px] text-[12px] text-blue-400 hover:bg-white/10"
							>
								{t('viewOnExplorer')} <ExternalLink className="h-3 w-3" />
							</a>
						)}
						<Button
							onClick={handleClose}
							className="mt-[8px] h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
						>
							{t('done')}
						</Button>
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	)
}
